/**
 * SellerLocationMap — chooses the seller-location map at runtime.
 *
 * Renders the real 2GIS `SellerMap` (MapGL) when a MapGL key is baked into the
 * build (`VITE_2GIS_API_KEY`) AND the seller + market have usable coordinates;
 * otherwise falls back to the keyless `MockedSellerMap` SVG stand-in. This lets
 * a keyed environment (QA with the demo key) show a live 2GIS map with the
 * seeded seller pin, while every other environment degrades gracefully — no
 * blank map, no hard dependency on the key.
 *
 * 2GIS is the ONLY map provider in GardEn (TRD §5.3 — never Mapbox/Google).
 * The seeded sellers already carry distinct Bishkek coordinates, so the pin
 * lands at a real (mocked) location on the live map.
 */
import type { ReactElement } from "react";

import SellerMap from "@/features/marketplace/SellerMap";
import { MockedSellerMap } from "@/features/marketplace/components/MockedSellerMap";
import type {
  ProductDetailMarket,
  ProductDetailSeller,
} from "@/features/marketplace/useProductDetail";

/** Coerce a Decimal-serialized string | number | null to a finite number or null. */
function toFiniteNumber(value: string | number | null): number | null {
  if (value === null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export interface SellerLocationMapProps {
  seller: ProductDetailSeller;
  market: ProductDetailMarket;
}

export function SellerLocationMap({
  seller,
  market,
}: SellerLocationMapProps): ReactElement {
  const apiKey = import.meta.env.VITE_2GIS_API_KEY;
  const hasKey = typeof apiKey === "string" && apiKey.length > 0;

  const sellerLat = toFiniteNumber(seller.lat);
  const sellerLng = toFiniteNumber(seller.lng);
  const marketLat = toFiniteNumber(market.lat);
  const marketLng = toFiniteNumber(market.lng);

  if (
    hasKey &&
    sellerLat !== null &&
    sellerLng !== null &&
    marketLat !== null &&
    marketLng !== null
  ) {
    return (
      <SellerMap
        sellerId={seller.id}
        sellerLat={sellerLat}
        sellerLng={sellerLng}
        marketCenterLat={marketLat}
        marketCenterLng={marketLng}
        marketName={market.name_ru}
        pavilionNumber={seller.pavilion_number}
      />
    );
  }

  return <MockedSellerMap seller={seller} market={market} />;
}

export default SellerLocationMap;
